import React from 'react'

import { useTranslation } from 'react-i18next'
import { SubMenu, SubMenuOptions } from 'Components/Elements/SubMenu/SubMenu'
import { isMobile } from 'react-device-detect'

import {
  MainCellStyled,
  MenuCells,
  MainCellTextStyled,
  MainMenuStyled,
  LogoWrapper,
  MenuSeparator,
  TopPart,
  MainMenuButton,
  ToggleMenuButton,
  BottomPart,
} from './MainMenu.styled'

import { Home } from 'Assets/icons/Home'
import { AdClick } from 'Assets/icons/AdClick'
import { AddCircle } from 'Assets/icons/AddCircle'
import { Globe } from 'Assets/icons/Globe'
import { MobileMenuHeader } from 'Components/Elements/MobileMenuHeader/MobileMenuHeader'
import { CQEssentialsLogo } from 'Assets/icons/CQEssentialsLogo'
import { Avatar } from 'Assets/icons/Avatar'
import { OpenMenuIcon } from 'Assets/icons/OpenMenuIcon'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { useNavigate, useLocation } from 'react-router-dom'
import { PAID_MARKETING_OPTIONS } from './PaidMarketing.models'
import {
  ButtonWithIcon,
  theme,
  CheqSmallIcon,
  StyledText,
} from '@clickcease/cc-client-components-lib'
import { CloseMenuIcon } from 'Assets/icons/CloseMenuIcon'
import botzappingRoutes from '../../botMitigation/moduleFederation/routes'
import { useGetPackagingLimits } from 'Features/accountSettings/api/getPackagingLimits'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGear } from '@fortawesome/pro-light-svg-icons'
import { useGetDomains } from 'Features/domainsManagement/api/getDomains'
import { GLOBAL_MODALS } from 'Types/GlobalModal'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { Domain } from 'Types/Domain'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { PRODUCT_TYPES } from 'Types/ProductTypes'
import { use12InstallmentsAllowChanges } from 'Hooks/split/use12InstallmentsChanges'
import { useIsTrialSubscription } from 'Hooks/trial/useIsTrialSubscription'
import useMainWrapperStore, { TRIGGER_OPTIONS } from 'Features/MainWrapper/stores/mainWrapper'

const botzappingRoutePrefix = 'botzapping'

export const MainMenu = () => {
  const { data: accountLimits } = useGetPackagingLimits()
  const { t } = useTranslation('mainMenu')
  const { setTriggerLocation } = useMainWrapperStore()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { owner, isAdmin, isInstallmentsSubscription } = useUserStore()
  const isTrialSubscription = useIsTrialSubscription()
  const { data: domains } = useGetDomains()
  const { data: plansDetails } = useGetPlansDetails()

  const menuDisabled = pathname === '/onboarding'
  const split12InstallmentsAllowChanges = use12InstallmentsAllowChanges()

  const installmentsSubscriptionAndNotOnTrialNoChangesAllowed =
    isInstallmentsSubscription && !isTrialSubscription && !split12InstallmentsAllowChanges

  const blockCrossSellActiveInstallmentsSubscription = plansDetails?.plans?.map((plan) =>
    plan.product.toLowerCase()
  )
  const isNoDomains = !domains || !domains.data.domains.length

  const noActiveDomainPM = (domains?.data.domains as Domain[])?.every(
    (domain) => domain.paidMarketing === false
  )
  const noActiveDomainBM = (domains?.data.domains as Domain[])?.every(
    (domain) => domain.botZapping === false
  )
  const isMarketingDisabled = (productType: string) =>
    isNoDomains ||
    menuDisabled ||
    (installmentsSubscriptionAndNotOnTrialNoChangesAllowed &&
      !blockCrossSellActiveInstallmentsSubscription?.includes(productType))

  const getDisabledMessage = (productType: string) =>
    isNoDomains || menuDisabled
      ? productType === PRODUCT_TYPES.PAID_MARKETING
        ? t('paid_marketing.disabled_message')
        : t('bot_mitigation_disabled_message')
      : installmentsSubscriptionAndNotOnTrialNoChangesAllowed
      ? t('disabled_message_schedule_meeting')
      : ''

  const botMitigationMarketingDisabled = isMarketingDisabled(PRODUCT_TYPES.BOT_ZAPPING)
  const paidMarketingMarketingDisabled = isMarketingDisabled(PRODUCT_TYPES.PAID_MARKETING)

  const botMitigationDisabledMessage = getDisabledMessage(PRODUCT_TYPES.BOT_ZAPPING)
  const paidMarketingDisabledMessage = getDisabledMessage(PRODUCT_TYPES.PAID_MARKETING)

  const { isMenuCollapsed, toggleMenuCollapsed, setModalToShow } = useAppStore()

  const renderMenuHeader = () => {
    if (isMobile) return <MobileMenuHeader />
    else if (isMenuCollapsed) return <CheqSmallIcon />
    else return <CQEssentialsLogo />
  }
  const renderMenuToggleIcon = () => (isMenuCollapsed ? <OpenMenuIcon /> : <CloseMenuIcon />)

  const goToPage = (url: string) => {
    navigate(`/${url}`)
  }

  const mappedBotzappingRoutes = botzappingRoutes.map<SubMenuOptions>(({ location, label }) => ({
    title: label,
    url: `${botzappingRoutePrefix}${location}`,
  }))

  const botzappingDefaultNavigationUrl = mappedBotzappingRoutes[0].url

  const goToDashboard = () => {
    if (!menuDisabled) goToPage('')
  }

  const openAddDomainModal = () => {
    setModalToShow(GLOBAL_MODALS.ADD_DOMAIN)
    setTriggerLocation(TRIGGER_OPTIONS.SIDEBAR)
  }

  return (
    <MainMenuStyled collapsed={isMenuCollapsed}>
      <TopPart collapsed={isMenuCollapsed}>
        <LogoWrapper collapsed={isMenuCollapsed} onClick={goToDashboard}>
          {renderMenuHeader()}
        </LogoWrapper>
        <MenuCells>
          <MainCellStyled
            disabled={isNoDomains || menuDisabled}
            collapsed={isMenuCollapsed}
            isActive={pathname === '/'}
            onClick={goToDashboard}
          >
            <Home />
            <MainCellTextStyled collapsed={isMenuCollapsed}>{t('home')}</MainCellTextStyled>
          </MainCellStyled>

          {!isMobile && <MenuSeparator collapsed={isMenuCollapsed} />}
          <SubMenu
            collapsed={isMenuCollapsed}
            isActive={pathname.includes('paid-marketing')}
            title={t('paid_marketing.title')}
            image={<AdClick />}
            disabled={paidMarketingMarketingDisabled}
            crossSell={!accountLimits?.paidMarketingAllowed ? '/paid-marketing-cross-sell' : ''}
            disabledMessage={paidMarketingDisabledMessage}
            options={PAID_MARKETING_OPTIONS({ owner, isAdmin }).filter(
              (option) => !option?.unallowedAccess
            )}
            defaultNavigationUrl="/paid-marketing/dashboard"
            navigateToDomainManagement={noActiveDomainPM ? '/domain-management' : ''}
            setCollapsed={toggleMenuCollapsed}
          />

          <SubMenu
            collapsed={isMenuCollapsed}
            isActive={pathname.includes('botzapping')}
            title={t('bot_mitigation')}
            image={<Globe width={20} />}
            disabled={botMitigationMarketingDisabled}
            crossSell={!accountLimits?.botZappingAllowed ? '/bot-mitigation-cross-sell' : ''}
            disabledMessage={botMitigationDisabledMessage}
            options={mappedBotzappingRoutes}
            defaultNavigationUrl={botzappingDefaultNavigationUrl}
            navigateToDomainManagement={noActiveDomainBM ? '/domain-management' : ''}
            setCollapsed={toggleMenuCollapsed}
          />

          <MenuSeparator collapsed={isMenuCollapsed} />
          <MainCellStyled
            isActive={pathname === '/domain-management'}
            onClick={() => {
              if (!menuDisabled) goToPage('domain-management')
            }}
            collapsed={isMenuCollapsed}
            className="domain-management"
            disabled={menuDisabled}
          >
            <FontAwesomeIcon icon={faGear as IconProp} />
            <MainCellTextStyled collapsed={isMenuCollapsed}>
              {isMenuCollapsed ? t('domain_management_collapsed') : t('domain_management')}
            </MainCellTextStyled>
          </MainCellStyled>
          {isMenuCollapsed && (
            <MainMenuButton onClick={openAddDomainModal}>
              <AddCircle color={theme.colors.primaryColor.value} width={22} height={22} />
              <StyledText
                variant={'xSmallRegular'}
                css={{ textAlign: 'center', color: '$primaryColor', marginTop: '$2' }}
              >
                {t('add_domain')}
              </StyledText>
            </MainMenuButton>
          )}
          {!isMenuCollapsed && owner && (
            <ButtonWithIcon
              variant="outline"
              disabled={menuDisabled}
              text={t('add_domain')}
              iconLeft={
                <AddCircle
                  color={
                    menuDisabled ? theme.colors.neutral.value : theme.colors.primaryColor.value
                  }
                  width={20}
                  height={20}
                />
              }
              onClick={openAddDomainModal}
              style={{ marginTop: '$8' }}
            />
          )}
          {isMobile && <MenuSeparator collapsed={isMenuCollapsed} />}

          {isMobile && (
            <MainCellStyled
              onClick={() => {
                goToPage('account-settings')
              }}
              collapsed={isMenuCollapsed}
              className="domain-management"
            >
              <Avatar />
              <MainCellTextStyled collapsed={isMenuCollapsed}>
                {t('account_settings')}
              </MainCellTextStyled>
            </MainCellStyled>
          )}
        </MenuCells>
      </TopPart>
      <BottomPart>
        <ToggleMenuButton
          collapsed={isMenuCollapsed}
          onClick={() => {
            if (!isMobile) toggleMenuCollapsed(!isMenuCollapsed)
          }}
        >
          {renderMenuToggleIcon()}
        </ToggleMenuButton>
      </BottomPart>
    </MainMenuStyled>
  )
}
